/**
 * CHAGUAL_ATC_PRICE_V1
 * Objetivo: En PDP mobile (<= 768px), mostrar el precio (y compare-at si aplica) junto al bloque de compra,
 * reordenando la UI para dejar "Cantidad" + "Precio" en una fila superior justo antes de los botones ATC.
 *
 * Qué hace:
 * - Sólo corre en mobile (matchMedia: '(max-width: 768px)').
 * - En `.purchase-details`:
 *   - Crea `.pdp-atc-toprow` (si no existe) e inserta ahí `.purchase-details__quantity`.
 *   - Crea `.pdp-atc-price` (si no existe) y renderiza:
 *     - Precio actual (span `.pdp-atc-price__current`)
 *     - Compare-at opcional (span `.pdp-atc-price__compare`) si es distinto y mayor al actual.
 *
 * Fuente de precios (prioridad):
 * 1) Variant activo: lee `[name="id"]` y cruza contra `meta.product.variants` o `ShopifyAnalytics.meta.product.variants`.
 *    - Formatea con CLP especial (es-CL) o `Shopify.formatMoney` si está disponible.
 * 2) Fallback DOM: busca texto de precio/compare-at con varios selectores `.price-ui`, `.product__price`, etc.
 *
 * Mantención/actualización:
 * - MutationObserver sobre el contenedor de producto para re-render en cambios de DOM/texto.
 *   Ignora las mutaciones del precio auxiliar que este mismo script genera.
 * - Listeners: `change` (capturing), `variant:change`, `variant:changed`.
 * - Reintentos de init (DOM ready + delays) para cubrir render tardío del theme/apps.
 *
 * Debug:
 * - `window.CHAGUAL_ATC_DEBUG()` imprime moneda activa, variantId y el objeto devuelto por findPrices().
 *
 * Nota: Usa guard `data-chagual-atc-price-init="1"` para evitar inicializar dos veces en la misma PDP.
 */



(() => {
  window.__CHAGUAL_ATC_PRICE_V1__ = (window.__CHAGUAL_ATC_PRICE_V1__ || 0) + 1;

  const onReady = (fn) => {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', fn, { once: true });
    } else fn();
  };

  const mq = window.matchMedia('(max-width: 768px)');
  const isMobile = () => !!mq.matches;

  const isVisible = (el) =>
    !!(el && (el.offsetWidth || el.offsetHeight || el.getClientRects().length));

  const looksLikeMoney = (txt) => /\d/.test(txt || '');

  const extractMoneyChunk = (txt) => {
    const t = String(txt || '').replace(/\s+/g, ' ').trim();
    if (!t) return '';
    const matches = t.match(/([$€£]?\s*\d[\d\.,\s]*\d|[$€£]?\s*\d)/g);
    if (!matches || !matches.length) return t;
    return matches[matches.length - 1].trim();
  };

    const findFormRoot = () =>
    document.querySelector('.product_form.ua-maceteros[data-product-form]') ||
    document.querySelector('.product_form.ua-maceteros');

  const pickText = (selectors, { requireVisible }) => {
    const root = findFormRoot() || document;
    for (const sel of selectors) {
      const el = root.querySelector(sel) || document.querySelector(sel);
      if (!el) continue;
      if (requireVisible && !isVisible(el)) continue;
      const txt = extractMoneyChunk(el.textContent || '');
      if (looksLikeMoney(txt)) return txt;
    }
    return '';
  };

  const getVariantId = () => {
    const root = findFormRoot() || document;
    const idEl =
      root.querySelector('form[action*="/cart/add"] [name="id"]') ||
      root.querySelector('input[name="id"]') ||
      document.querySelector('form[action*="/cart/add"] [name="id"]') ||
      document.querySelector('input[name="id"]');
    return idEl?.value ? String(idEl.value) : '';
  };

  const getVariants = () => {
    const v1 = window?.meta?.product?.variants;
    if (Array.isArray(v1)) return v1;

    const v2 = window?.ShopifyAnalytics?.meta?.product?.variants;
    if (Array.isArray(v2)) return v2;

    return [];
  };

  const getActiveCurrency = () =>
    window?.Shopify?.currency?.active ||
    window?.Shopify?.currency?.currency ||
    window?.ShopifyAnalytics?.meta?.currency ||
    window?.ShopifyAnalytics?.meta?.currency_code ||
    '';

  const formatCLP = (cents) => {
    const n = Math.round(Number(cents) / 100);
    return `$${n.toLocaleString('es-CL')}`;
  };

  const formatMoney = (cents) => {
    const active = String(getActiveCurrency() || '').toUpperCase();
    if (active === 'CLP') return formatCLP(cents);

    const nCents = Number(cents);
    if (Number.isFinite(nCents) && window.Shopify && typeof window.Shopify.formatMoney === 'function') {
      const fmt = window.theme?.moneyFormat || window.Shopify.money_format || '${{amount}}';
      return window.Shopify.formatMoney(nCents, fmt);
    }
    const n = Math.round((Number.isFinite(nCents) ? nCents : 0) / 100);
    return `$${n.toLocaleString('en-US')}`;
  };

  const getCurrentVariant = () => {
    const vid = getVariantId();
    if (!vid) return null;
    const variants = getVariants();
    return variants.find((x) => String(x.id) === String(vid)) || null;
  };

  const findFromDOM = () => {
    const currentText = pickText(
      [
        '.price-ui [data-price] .money',
        '.price-ui [data-price]',
        '[data-price-ui] [data-price] .money',
        '[data-price-ui] [data-price]',
        '.product__price .sale_price .money',
        '.product__price .current_price .money',
        '.product__price .price__current .money',
        '.product__price ins .money',
        '[data-product-price] .money',
        '.product__price .money',
        '.price .money',
      ],
      { requireVisible: true }
    );

    const compareText = pickText(
      [
        '.price-ui [data-compare-at-price] .money',
        '.price-ui [data-compare-at-price]',
        '[data-price-ui] [data-compare-at-price] .money',
        '[data-price-ui] [data-compare-at-price]',
        '.product__price .was_price .money',
        '.product__price .was_price',
        '.product__price .compare-at-price .money',
        '.product__price .compare-at-price',
        '.product__price del .money',
        '.product__price del',
        '.product__price s .money',
        '.product__price s',
      ],
      { requireVisible: false }
    );

    const formRoot = findFormRoot();
    const root =
      (formRoot && formRoot.closest('.product-main, .product_section, .product_section-js, .product-page, [data-product-id], .section')) ||
      document.querySelector('[data-price-ui], .price-ui, .product__price, .product__price_wrap, .price, [data-product-price]') ||
      document.body;

    return { currentText, compareText, root };
  };

  const findPrices = () => {
    const dom = findFromDOM();

    const v = getCurrentVariant();
    if (v && Number(v.price)) {
      const priceCents = Number(v.price);
      const compareCents = Number(v.compare_at_price || 0);
      const hasCompare = Number.isFinite(compareCents) && compareCents > 0;

      const currentText = formatMoney(priceCents);

      let compareText = '';
      if (hasCompare && compareCents > priceCents) {
        compareText = formatMoney(compareCents);
      } else if (dom.compareText && dom.compareText.trim() && dom.compareText.trim() !== dom.currentText.trim()) {
        compareText = dom.compareText.trim();
      }

      const root = dom.root || document.body;

      return { currentText, compareText, root, source: 'variant+dom', variant: v };
    }

    return { ...dom, source: 'dom', variant: null };
  };

  const renderPrice = (priceEl) => {
    const { currentText, compareText } = findPrices();
    if (!currentText) return;

    const nextCurrent = currentText.trim();
    const compareCandidate = (compareText || '').trim();
    const nextCompare = compareCandidate && compareCandidate !== nextCurrent ? compareCandidate : '';
    const renderedCurrent = priceEl.querySelector('.pdp-atc-price__current')?.textContent?.trim() || '';
    const renderedCompare = priceEl.querySelector('.pdp-atc-price__compare')?.textContent?.trim() || '';

    if (renderedCurrent === nextCurrent && renderedCompare === nextCompare) return;

    priceEl.replaceChildren();

    const cur = document.createElement('span');
    cur.className = 'pdp-atc-price__current';
    cur.textContent = nextCurrent;
    priceEl.appendChild(cur);

    if (nextCompare) {
      const cmp = document.createElement('span');
      cmp.className = 'pdp-atc-price__compare';
      cmp.textContent = nextCompare;
      priceEl.appendChild(cmp);
    }
  };

  const onlyMutatesRenderedPrice = (mutations, priceEl) =>
    mutations.length > 0 &&
    mutations.every(({ target }) => target === priceEl || priceEl.contains(target));

  const safeInsertBefore = (parent, node, reference) => {
    if (!parent || !node || !reference) return false;
    if (reference.parentNode !== parent) return false;

    try {
      parent.insertBefore(node, reference);
      return true;
    } catch (error) {
      return false;
    }
  };

  const init = () => {
    if (!isMobile()) return;

    const formRoot = findFormRoot();
    if (!formRoot) return;

    const pd = formRoot.querySelector('.purchase-details');
    if (!pd || pd.dataset.chagualAtcPriceInit === '1') return;
    pd.dataset.chagualAtcPriceInit = '1';

    const qty = pd.querySelector('.purchase-details__quantity');
    const buttons = pd.querySelector('.purchase-details__buttons');
    if (!qty || !buttons) return;

    let top = pd.querySelector('.pdp-atc-toprow');
    if (!top) {
      top = document.createElement('div');
      top.className = 'pdp-atc-toprow';
      if (!safeInsertBefore(pd, top, buttons)) return;
    }

    if (qty.parentElement !== top) top.appendChild(qty);

    let price = pd.querySelector('.pdp-atc-price');
    if (!price) {
      price = document.createElement('div');
      price.className = 'pdp-atc-price';
      top.appendChild(price);
    }

    renderPrice(price);

    const { root } = findPrices();
    const mo = new MutationObserver((mutations) => {
      if (onlyMutatesRenderedPrice(mutations, price)) return;
      renderPrice(price);
    });
    mo.observe(root, { childList: true, subtree: true, characterData: true });

    const rerender = () => setTimeout(() => renderPrice(price), 0);
    document.addEventListener('change', rerender, true);
    document.addEventListener('variant:change', rerender);
    document.addEventListener('variant:changed', rerender);
  };

  onReady(() => {
    init();
    setTimeout(init, 250);
    setTimeout(init, 800);
  });

  mq.addEventListener?.('change', () => {
    if (isMobile()) init();
  });

  window.CHAGUAL_ATC_DEBUG = () => {
    const fp = findPrices();
    console.log('[CHAGUAL_ATC_DEBUG] mobile=', isMobile());
    console.log('[CHAGUAL_ATC_DEBUG] currency.active=', getActiveCurrency());
    console.log('[CHAGUAL_ATC_DEBUG] Shopify.money_format=', window.Shopify?.money_format);
    console.log('[CHAGUAL_ATC_DEBUG] variantId=', getVariantId());
    console.log('[CHAGUAL_ATC_DEBUG] findPrices=', fp);
    return fp;
  };


  

  


})();

